import { AsyncPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxAmountForm } from '@onyx/angular';
import {
  FormHelper,
  ONYX_INPUT_DEBOUNCE_TIME,
  OnyxAmountPipe,
  OnyxButtonComponent,
  OnyxDatepickerComponent,
  OnyxDatePipe,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  OnyxInformationHeadingComponent,
  onyxMaxDateValidator,
  onyxMinDateValidator,
  OnyxSuggestionsComponent,
  OnyxTextFieldComponent,
  onyxTextFieldLengthValidator,
  OnyxToastService,
  OnyxUploadComponent,
} from '@onyx/angular';
import { chain, isEqual, isObject, sumBy, uniq } from 'lodash';
import { DateTime } from 'luxon';
import type { Observable } from 'rxjs';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  map,
  of,
  switchMap,
  take,
} from 'rxjs';
import { DictionaryCode } from '../../../../../../../common/enums/dictionary-code';
import { CommonHelper } from '../../../../../../../common/helpers/common.helper';
import { SuggestionHelper } from '../../../../../../../common/helpers/suggestions.helper';
import { ValidationHelper } from '../../../../../../../common/helpers/validation.helper';
import { AmountService } from '../../../../../../../common/services/amount.service';
import { DictionariesService } from '../../../../../../../common/services/dictionaries.service';
import { PreferencesService } from '../../../../../../../common/services/preferences.service';
import type { Order } from '../../../../../common/interfaces/order';
import { OrdersService } from '../../../../../common/services/orders.service';

type OrderModalInvoiceFormGroup = ReturnType<
  OrderModalInvoiceFormComponent['buildForm']
>;
type OrderModalInvoiceForm = ReturnType<
  OrderModalInvoiceFormGroup['getRawValue']
>;
export type OrderModalInvoiceFormDto = Omit<
  OrderModalInvoiceForm,
  'nbpRateDate'
>;

@Component({
  selector: 'app-order-modal-invoice-form',
  imports: [
    OnyxDatepickerComponent,
    OnyxTextFieldComponent,
    OnyxDropdownComponent,
    OnyxUploadComponent,
    OnyxButtonComponent,
    OnyxFormGroupComponent,
    TranslatePipe,
    ReactiveFormsModule,
    AsyncPipe,
    OnyxSuggestionsComponent,
    OnyxAmountPipe,
    OnyxInformationHeadingComponent,
  ],
  templateUrl: './order-modal-invoice-form.component.html',
  styleUrl: './order-modal-invoice-form.component.scss',
})
export class OrderModalInvoiceFormComponent implements OnInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly preferencesService = inject(PreferencesService);
  private readonly toastService = inject(OnyxToastService);
  private readonly datePipe = inject(OnyxDatePipe);
  private readonly ordersService = inject(OrdersService);
  private readonly amountService = inject(AmountService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly injector = inject(Injector);

  public readonly order = input.required<Order>();

  public readonly hideForm = output<void>();

  protected readonly I18N = 'orders.orderModal';
  protected readonly SELL_DATE_SUGGESTIONS =
    SuggestionHelper.getDateSuggestions(this.translateService, this.datePipe);

  protected readonly currencies$ = this.dictionariesService.getDictionary(
    DictionaryCode.CURRENCY,
  );

  protected form = this.buildForm();
  protected loading = signal(false);
  protected grossResult = signal<OnyxAmountForm[]>([]);
  protected sellDate = signal<string | null>(null);

  protected paymentTermSuggestions = computed(() => {
    const sellDate = this.sellDate();
    if (!sellDate) return [];

    const payment = this.order().payment;
    const contractorPayment = payment.contractorPayment;

    return SuggestionHelper.getPaymentTermSuggestions(
      sellDate,
      payment.contractorInvoice.shippingType.traditionalMail,
      contractorPayment.termDays,
      contractorPayment.termType,
      contractorPayment.selectedTermDay,
      this.datePipe,
    );
  });

  constructor() {
    const controls = this.form.controls;

    const sellDate$ = controls.sellDate.valueChanges.pipe(
      distinctUntilChanged(),
    );
    sellDate$.pipe(take(1)).subscribe((value) => {
      if (!value) return;

      const previousDay = DateTime.fromISO(value)
        .minus({ days: 1 })
        .toISODate();

      if (controls.nbpRateDate.dirty) return;

      controls.nbpRateDate.setValue(previousDay);
    });
    sellDate$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.sellDate.set(value);

      const paymentTerm = this.paymentTermSuggestions()?.[0]?.value;
      const date = value ? DateTime.fromISO(value) : null;
      const validators = date?.isValid
        ? [Validators.required, onyxMinDateValidator(date)]
        : [Validators.required];

      if (paymentTerm) controls.paymentTerm.setValue(paymentTerm);
      controls.paymentTerm.setValidators(validators);
      controls.paymentTerm.updateValueAndValidity();
    });

    controls.nbpRateDate.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(async (value) => {
        if (await ValidationHelper.checkValidity(controls.nbpRateDate, null)) {
          controls.net.patchValue({ date: value });
          controls.vat.patchValue({ date: value });
        }

        ValidationHelper.toggleControls([controls.vat, controls.net], {
          enabled: !!value && controls.nbpRateDate.valid,
          reset: true,
        });
      });

    combineLatest([
      controls.nbpRateDate.valueChanges,
      controls.net.valueChanges,
      controls.vat.valueChanges,
    ])
      .pipe(
        map((values) => values.filter((value) => isObject(value))),
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        debounceTime(ONYX_INPUT_DEBOUNCE_TIME),
        map((amounts) =>
          chain(amounts)
            .reduce(
              (result, { value, currency }) => {
                if (value != null && currency) {
                  result[currency] = (result[currency] ?? 0) + value;
                }
                return result;
              },
              {} as Record<string, number>,
            )
            .entries()
            .value(),
        ),
        switchMap((amounts): Observable<OnyxAmountForm[]> => {
          if (!amounts.length) return of([]);

          const rateDate = controls.nbpRateDate.getRawValue();
          const calculateAmount$ = (
            value: number,
            currency: string,
            resultCurrency: string,
          ): Observable<OnyxAmountForm> => {
            if (currency === resultCurrency) {
              return of({ value, currency, date: rateDate });
            }

            return this.amountService
              .calculateAmount({
                date: rateDate,
                value,
                currency,
                resultCurrency,
              })
              .pipe(
                map(({ resultCurrencyValue, resultCurrency, date }) => ({
                  value: resultCurrencyValue!,
                  currency: resultCurrency!,
                  date,
                })),
              );
          };

          const defaultCurrency = this.order().price.defaultCurrency;
          const resultCurrencies = uniq([
            defaultCurrency,
            ...amounts.map(([currency]) => currency),
          ]);

          const results$ = amounts.flatMap(([currency, value]) =>
            resultCurrencies.map((resultCurrency) =>
              calculateAmount$(value, currency, resultCurrency),
            ),
          );
          return forkJoin(results$).pipe(
            map((results) =>
              chain(results)
                .groupBy((result) => result.currency)
                .mapValues((values) => ({
                  ...values[0],
                  value: sumBy(values, (value) => value.value),
                }))
                .values()
                .value(),
            ),
          );
        }),
      )
      .subscribe((result) => this.grossResult.set(result));
  }

  public ngOnInit(): void {
    const controls = this.form.controls;
    const { price, basicInformation } = this.order();

    for (const control of [controls.net, controls.vat]) {
      control.controls.currency.setValue(price.currency);
    }

    controls.net.controls.value.setValue(price.value);
    controls.vat.controls.value.setValue(
      CommonHelper.calculateVat(price.value, basicInformation.vatRate),
    );
  }

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const { nbpRateDate: _, ...dto } = this.form.getRawValue();

    this.loading.set(true);
    this.ordersService
      .addInvoice(this.order().uuid, dto)
      .subscribe(() =>
        this.toastService.showSuccess({
          message: `${this.I18N}.toasts.invoiceAdded`,
          keepOnNavigation: true,
        }),
      )
      .add(() => this.loading.set(false));
  }

  private buildForm() {
    return this.fb.group({
      number: this.fb.control<string | null>(null, [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
      sellDate: this.fb.control<string | null>(null, [Validators.required]),
      paymentTerm: this.fb.control<string | null>(null, [Validators.required]),
      nbpRateDate: this.fb.control<string | null>(null, [
        Validators.required,
        onyxMaxDateValidator(),
      ]),
      net: FormHelper.buildAmountForm(this.fb, this.preferencesService, {
        minValue: Number.EPSILON,
        disabled: true,
      }),
      vat: FormHelper.buildAmountForm(this.fb, this.preferencesService, {
        disabled: true,
      }),
      scan: this.fb.control<string | null>(null),
    });
  }
}
